import { normalizeError } from '../../../utils';
import {
  AUDIO_CAPTURE_FAILED_SUBCODE_MESSAGE,
  AudioCaptureFailedSubCode,
  ExecutedFailure,
  ZoomErrorCode,
} from '../types';

function describeFailure(failure: ExecutedFailure): string {
  return `${failure.type} | ${failure.errorCode} | ${failure.reason}`;
}

export class ZoomExecutedFailureError extends Error implements ExecutedFailure {
  type;
  errorCode;
  reason;

  constructor(failure: ExecutedFailure, message?: string) {
    super(message ?? `ZoomExecutedFailureError: ${describeFailure(failure)}`);
    this.type = failure.type;
    this.errorCode = failure.errorCode;
    this.reason = failure.reason;
  }
}

export type AudioCaptureFailure = ExecutedFailure & { subCode: number };

/**
 * Specialized failure for `AUDIO_CAPTURE_FAILED` rejections, the only Zoom failure that carries a `subCode`.
 */
export class AudioCaptureFailedError extends ZoomExecutedFailureError {
  declare errorCode: (typeof ZoomErrorCode)['AUDIO_CAPTURE_FAILED'];
  subCode: AudioCaptureFailedSubCode;

  constructor(failure: AudioCaptureFailure) {
    const subCodeDescription = AUDIO_CAPTURE_FAILED_SUBCODE_MESSAGE[failure.subCode as AudioCaptureFailedSubCode];
    const description = `AudioCaptureFailedError: ${describeFailure(failure)} | ${subCodeDescription}`;
    super(failure, description);

    this.subCode = failure.subCode;
  }
}

/**
 * ExecutedFailure is only a type definition.
 * "reason" is a required parameter, but is optional in some return values.
 */
export function isZoomExecutedFailure(error: unknown): error is ExecutedFailure {
  return typeof error === 'object' && error !== null && 'type' in error && 'errorCode' in error;
}

function isAudioCaptureFailure(failure: ExecutedFailure): failure is AudioCaptureFailure {
  return failure.errorCode === ZoomErrorCode.AUDIO_CAPTURE_FAILED && failure.subCode !== undefined;
}

export function toZoomExecutedFailureError(failure: ExecutedFailure): ZoomExecutedFailureError {
  return isAudioCaptureFailure(failure) ? new AudioCaptureFailedError(failure) : new ZoomExecutedFailureError(failure);
}

/**
 * Returns a Zoom error code and {@link ExecutedFailure} tuple if the error is a
 * Zoom failure, otherwise returns a normalized error.
 */
export function toZoomError(error: unknown): [number, ExecutedFailure] | [null, Error] {
  if (isZoomExecutedFailure(error)) {
    return [error.errorCode, error];
  }

  return [null, normalizeError(error)];
}
